/**
 * Test Data Capture for AutoPark Debugging
 *
 * Automatically captures all inputs/outputs when AutoPark runs, detects duplicates,
 * and generates ready-to-use unit test code for reproduction.
 */

import { Placement, ParkingRequest, Spot, Zone } from "./types";

/**
 * Duplicate detection result
 */
export interface DuplicateDetection {
  hasDuplicates: boolean;
  duplicateAircraft: Array<{
    requestId: string;
    tailNumber: string;
    spots: Array<{ spotId: string; startBucket: number; endBucket: number }>;
  }>;
  overlappingSpots: Array<{
    spotId: string;
    aircraft: Array<{
      requestId: string;
      tailNumber: string;
      startBucket: number;
      endBucket: number;
    }>;
  }>;
}

/**
 * Detect duplicates in placement results
 *
 * Checks for two types of bugs:
 * 1. Same aircraft placed in multiple spots at overlapping times
 * 2. Multiple aircraft in same spot at same time
 */
export function detectDuplicates(
  placement: Placement,
  requests: ParkingRequest[]
): DuplicateDetection {
  const result: DuplicateDetection = {
    hasDuplicates: false,
    duplicateAircraft: [],
    overlappingSpots: [],
  };

  // Build request lookup map for tail numbers
  const requestMap = new Map<string, ParkingRequest>();
  requests.forEach((req) => {
    requestMap.set(req.id, req);
  });

  // Check 1: Same aircraft placed multiple times
  // (Single-spot placements should never duplicate, but multi-segment might have bugs)
  const aircraftSpots = new Map<
    string,
    Array<{ spotId: string; startBucket: number; endBucket: number }>
  >();

  for (const [requestId, placementData] of Object.entries(placement)) {
    const spots: Array<{
      spotId: string;
      startBucket: number;
      endBucket: number;
    }> = [];

    if ("spotId" in placementData) {
      // Single-spot placement
      const request = requestMap.get(requestId);
      if (request) {
        spots.push({
          spotId: placementData.spotId,
          startBucket: request.arrivalBucket,
          endBucket: request.departureBucket,
        });
      }
    } else {
      // Multi-segment placement
      for (const seg of placementData.segments) {
        spots.push({
          spotId: seg.spotId,
          startBucket: seg.startBucket,
          endBucket: seg.endBucket,
        });
      }
    }

    aircraftSpots.set(requestId, spots);
  }

  // Check for aircraft with overlapping time ranges in different spots
  for (const [requestId, spots] of aircraftSpots.entries()) {
    if (spots.length > 1) {
      // Check if any segments overlap in time
      const hasOverlap = spots.some((spot1, i) =>
        spots.slice(i + 1).some((spot2) => {
          const overlap =
            spot1.startBucket < spot2.endBucket &&
            spot2.startBucket < spot1.endBucket;
          return overlap;
        })
      );

      if (hasOverlap) {
        const request = requestMap.get(requestId);
        result.hasDuplicates = true;
        result.duplicateAircraft.push({
          requestId,
          tailNumber: request?.tail_number || "Unknown",
          spots,
        });
      }
    }
  }

  // Check 2: Multiple aircraft in same spot at same time
  // Build spot → aircraft map with time ranges
  const spotOccupancy = new Map<
    string,
    Array<{
      requestId: string;
      tailNumber: string;
      startBucket: number;
      endBucket: number;
    }>
  >();

  for (const [requestId, placementData] of Object.entries(placement)) {
    const request = requestMap.get(requestId);
    const tailNumber = request?.tail_number || "Unknown";

    if ("spotId" in placementData) {
      // Single-spot placement
      const spotId = placementData.spotId;
      if (!spotOccupancy.has(spotId)) {
        spotOccupancy.set(spotId, []);
      }
      if (request) {
        spotOccupancy.get(spotId)!.push({
          requestId,
          tailNumber,
          startBucket: request.arrivalBucket,
          endBucket: request.departureBucket,
        });
      }
    } else {
      // Multi-segment placement
      for (const seg of placementData.segments) {
        if (!spotOccupancy.has(seg.spotId)) {
          spotOccupancy.set(seg.spotId, []);
        }
        spotOccupancy.get(seg.spotId)!.push({
          requestId,
          tailNumber,
          startBucket: seg.startBucket,
          endBucket: seg.endBucket,
        });
      }
    }
  }

  // Check each spot for overlapping aircraft
  for (const [spotId, aircraft] of spotOccupancy.entries()) {
    if (aircraft.length > 1) {
      // Check if any aircraft overlap in time
      for (let i = 0; i < aircraft.length; i++) {
        for (let j = i + 1; j < aircraft.length; j++) {
          const a1 = aircraft[i];
          const a2 = aircraft[j];
          const overlap =
            a1.startBucket < a2.endBucket && a2.startBucket < a1.endBucket;

          if (overlap) {
            result.hasDuplicates = true;
            // Check if already added
            if (
              !result.overlappingSpots.some(
                (s) => s.spotId === spotId && s.aircraft.length === aircraft.length
              )
            ) {
              result.overlappingSpots.push({
                spotId,
                aircraft,
              });
            }
            break;
          }
        }
      }
    }
  }

  return result;
}

/**
 * Serialize spot for test data (minimal representation)
 * Removes occupancy bitmap (runtime state) and includes only essential fields
 */
function serializeSpot(spot: Spot): string {
  return `{
    id: "${spot.id}",
    zoneId: "${spot.zoneId}",
    allowedSizes: [${spot.allowedSizes.map((s) => `"${s}"`).join(", ")}],
    orientations: [${spot.orientations.map((o) => `{ id: "${o.id}" }`).join(", ")}],
    max_dwell_minutes: ${spot.max_dwell_minutes ?? "undefined"},
    disabled: ${spot.disabled ?? false},
    ${
      spot.egressOptions
        ? `egressOptions: [${spot.egressOptions
            .map(
              (opt) =>
                `{ laneId: "${opt.laneId}", laneName: "${opt.laneName}", blocksSpotIds: [${opt.blocksSpotIds.map((id) => `"${id}"`).join(", ")}] }`
            )
            .join(", ")}],`
        : ""
    }
  }`;
}

/**
 * Serialize parking request for test data
 */
function serializeRequest(request: ParkingRequest): string {
  return `{
    id: "${request.id}",
    tail_number: "${request.tail_number || "Unknown"}",
    sizeCategory: "${request.sizeCategory}",
    arrivalBucket: ${request.arrivalBucket},
    departureBucket: ${request.departureBucket},
    aircraft: {
      wingspan: ${request.aircraft?.wingspan ?? 0},
      length: ${request.aircraft?.length ?? 0},
    },
  }`;
}

/**
 * Serialize zone for test data
 */
function serializeZone(zone: Zone): string {
  return `{
    id: "${zone.id}",
    name: "${zone.name}",
    spotIds: [${zone.spotIds.map((id) => `"${id}"`).join(", ")}],
    priority: ${zone.priority ?? 5},
    disabled: ${zone.disabled ?? false},
  }`;
}

/**
 * Generate unit test code from captured data
 */
export function generateTestCode(
  requests: ParkingRequest[],
  spots: Spot[],
  zones: Zone[],
  config: {
    bufferMinutes: number;
    plannedMoveCap: number;
    allowTrapping: boolean;
    egressWeight: number;
    wasteWeight: number;
    zonePriorityWeight: number;
    horizonHours: number;
  },
  placement: Placement,
  duplicateInfo: DuplicateDetection
): string {
  const testName = `reproduces duplicate placement bug (${new Date().toISOString()})`;

  return `
/**
 * AUTO-GENERATED TEST DATA
 * Captured from AutoPark run at ${new Date().toISOString()}
 *
 * Duplicate Detection:
${duplicateInfo.duplicateAircraft.map((dup) => ` * - Aircraft ${dup.tailNumber} (${dup.requestId}) placed in ${dup.spots.length} spots`).join("\n")}
${duplicateInfo.overlappingSpots.map((overlap) => ` * - Spot ${overlap.spotId} has ${overlap.aircraft.length} overlapping aircraft`).join("\n")}
 */

import { parkingAlgorithm } from "../parkingAlgorithm";
import { detectDuplicates } from "../testDataCapture";
import { makeEmptyMask } from "../bitmap";
import { ParkingRequest, Spot, Zone } from "../types";

describe("AutoPark duplicate bug", () => {
  it("${testName}", () => {
    // Test data captured from production run
    const horizonHours = ${config.horizonHours};
    const totalBuckets = horizonHours * 4;

    const requests: ParkingRequest[] = [
${requests.map((req) => "      " + serializeRequest(req)).join(",\n")}
    ];

    const spots: Spot[] = [
${spots.map((spot) => "      " + serializeSpot(spot)).join(",\n")}
    ].map(spot => ({
      ...spot,
      occupancy: makeEmptyMask(totalBuckets),
    }));

    const zones: Zone[] = [
${zones.map((zone) => "      " + serializeZone(zone)).join(",\n")}
    ];

    // Run algorithm with captured config
    const placement = parkingAlgorithm(
      requests,
      spots,
      ${config.bufferMinutes}, // bufferMinutes
      ${config.plannedMoveCap}, // plannedMoveCap
      ${config.allowTrapping}, // allowTrapping
      ${config.egressWeight}, // egressWeight
      ${config.wasteWeight}, // wasteWeight
      0.1, // structuralWeight
      "arrival", // sortingStrategy
      horizonHours,
      undefined, // shapes
      false, // useMaskOptimization
      false, // validateMasks
      ${config.zonePriorityWeight}, // zonePriorityWeight
      zones
    );

    // Detect duplicates
    const duplicateInfo = detectDuplicates(placement, requests);

    // Log duplicates for debugging
    if (duplicateInfo.hasDuplicates) {
      console.log("=== DUPLICATES DETECTED ===");
      console.log("Duplicate Aircraft:", JSON.stringify(duplicateInfo.duplicateAircraft, null, 2));
      console.log("Overlapping Spots:", JSON.stringify(duplicateInfo.overlappingSpots, null, 2));
    }

    // Assert no duplicates
    expect(duplicateInfo.hasDuplicates).toBe(false);
    expect(duplicateInfo.duplicateAircraft).toHaveLength(0);
    expect(duplicateInfo.overlappingSpots).toHaveLength(0);
  });
});
`;
}

/**
 * Capture test data when AutoPark runs and log if duplicates detected
 *
 * This function should be called after parkingAlgorithm() runs in production.
 * It automatically detects duplicates and logs ready-to-use test code.
 */
export function captureTestData(
  requests: ParkingRequest[],
  spots: Spot[],
  zones: Zone[],
  config: {
    bufferMinutes: number;
    plannedMoveCap: number;
    allowTrapping: boolean;
    egressWeight: number;
    wasteWeight: number;
    zonePriorityWeight: number;
    horizonHours: number;
  },
  placement: Placement
): void {
  // Detect duplicates
  const duplicateInfo = detectDuplicates(placement, requests);

  // Only log if duplicates detected
  if (duplicateInfo.hasDuplicates) {
    console.error("=== ⚠️  DUPLICATE PLACEMENT DETECTED IN AUTOPARK ===");
    console.error(`Found ${duplicateInfo.duplicateAircraft.length} aircraft with duplicate placements`);
    console.error(`Found ${duplicateInfo.overlappingSpots.length} spots with overlapping aircraft`);

    // Log details
    duplicateInfo.duplicateAircraft.forEach((dup) => {
      console.error(
        `  - Aircraft ${dup.tailNumber} (${dup.requestId}) placed in ${dup.spots.length} spots:`
      );
      dup.spots.forEach((spot) => {
        console.error(
          `    - Spot ${spot.spotId}: buckets ${spot.startBucket}-${spot.endBucket}`
        );
      });
    });

    duplicateInfo.overlappingSpots.forEach((overlap) => {
      console.error(
        `  - Spot ${overlap.spotId} has ${overlap.aircraft.length} overlapping aircraft:`
      );
      overlap.aircraft.forEach((ac) => {
        console.error(
          `    - ${ac.tailNumber} (${ac.requestId}): buckets ${ac.startBucket}-${ac.endBucket}`
        );
      });
    });

    // Generate test code
    const testCode = generateTestCode(
      requests,
      spots,
      zones,
      config,
      placement,
      duplicateInfo
    );

    console.error("\n=== COPY THIS TEST CODE TO REPRODUCE ===");
    console.error(
      "File: app/src/screens/Ramp/TemporalTest/__tests__/duplicateBug.test.ts"
    );
    console.error(testCode);
    console.error("=== END TEST CODE ===\n");
  }
}
